<script setup lang="ts">
import { assetLink } from "@/common/util"
import type { Article } from "@/model/backend/article"
import { useShopAppConfig } from "@/shop/composables"
import { useAuthentication } from "@/shop/composables/useAuthentication"
import { useRouter } from "vue-router"

/*─────────────────────────────────────┐
│   functions                          │
└─────────────────────────────────────*/
async function loginAndGoToArticle(article: Article) {
    if (await auth.successfulLogin()) {
        router.push({ name: "article", params: { urlId: article.urlId, slug: article.slug } })
    }
}

/*─────────────────────────────────────┐
│   composables                        │
└─────────────────────────────────────*/
const { appConfig } = useShopAppConfig()
const auth = useAuthentication()
const router = useRouter()
/*─────────────────────────────────────┐
│   setup                              │
└─────────────────────────────────────*/
</script>

<template>
    <main aria-labelledby="welcome">
        <div class="container py-md-5">
            <div class="teaser">
                <article class="teaser-text" role="article" aria-labelledby="cannabis">
                    <h3 id="cannabis">Medizinisches Cannabis</h3>

                    <p>
                        Moin und Herzlich Willkommen bei CANN24! Wir sind deine Apotheke für medizinisches Cannabis und
                        versorgen dich deutschlandweit per Versand und natürlich auch direkt vor Ort in
                        Hamburg-Wilhelmsburg in der apotheke am inselpark.
                    </p>

                    <RouterLink class="btn btn-primary" :to="{ name: 'articles' }">Zum Sortiment</RouterLink>
                </article>

                <div class="teaser-image" aria-hidden="true">
                    <img src="./start01.jpg" alt="" class="img-fluid" />
                </div>
            </div>
        </div>
        <div v-if="appConfig.startPageFeatured" class="container">
            <div class="teaser pt-md-5">
                <div class="teaser-image" aria-hidden="true">
                    <img
                        class="img-fluid"
                        :src="assetLink('/uploads/media/' + appConfig.startPageFeatured.imageName)"
                        alt=""
                    />
                </div>

                <article class="teaser-text" aria-labelledby="featured-title">
                    <h2>{{ appConfig.startPageFeatured.subtitle }}</h2>
                    <h3 id="featured-title">{{ appConfig.startPageFeatured.title }}</h3>

                    <div v-html="appConfig.startPageFeatured.text"></div>

                    <RouterLink
                        v-if="appConfig.startPageFeatured.visible"
                        class="btn btn-primary"
                        :to="{
                            name: 'article',
                            params: {
                                urlId: appConfig.startPageFeatured.article.urlId,
                                slug: appConfig.startPageFeatured.article.slug,
                            },
                        }"
                    >
                        Zum Artikel
                    </RouterLink>

                    <button
                        v-if="!appConfig.startPageFeatured.visible"
                        class="btn btn-primary"
                        @click="loginAndGoToArticle(appConfig.startPageFeatured.article)"
                    >
                        Zum Artikel
                    </button>
                </article>
            </div>
        </div>

        <div v-if="appConfig.startPageArticles?.length" class="container-fluid bg-teaser-secondary">
            <div class="container">
                <div class="teaser teaser-secondary">
                    <article class="teaser-text pr-md-3" aria-labelledby="sortiment">
                        <h3 id="sortiment">Sortiment</h3>

                        <p>
                            Auszug aus unserem Sortiment. Alle Verfügbarkeiten findest du
                            <RouterLink :to="{ name: 'articles' }">hier</RouterLink>.
                        </p>

                        <RouterLink class="btn btn-primary" :to="{ name: 'articles' }">Zum Sortiment</RouterLink>
                    </article>

                    <div class="teaser-image" style="order: 3 !important">
                        <div class="product-teaser-cards">
                            <RouterLink
                                v-for="article in appConfig.startPageArticles"
                                :key="article.id"
                                class="product-teaser-card"
                                :to="{ name: 'article', params: { urlId: article.urlId, slug: article.slug } }"
                            >
                                <img
                                    v-if="article.brand?.logoName"
                                    :src="assetLink(`/uploads/media/${article.brand?.logoName}`)"
                                    :alt="article.brand?.name"
                                    aria-hidden="true"
                                />
                                <div class="product-teaser-card-name">
                                    {{ article.publicNameSecondary }}
                                </div>
                                <div class="product-teaser-card-details">
                                    {{ article.species }} &middot;
                                    <span class="text-nowrap">THC {{ article.thc }} %</span>
                                </div>
                                <div class="product-teaser-card-hover">
                                    <button class="btn btn-primary" tabindex="-1">Zum Produkt</button>
                                </div>
                            </RouterLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="container">
            <div class="teaser py-md-5 py-4 my-md-5">
                <div class="teaser-image" aria-hidden="true">
                    <img src="./start02.jpg" alt="" class="img-fluid" />
                </div>

                <article class="teaser-text" aria-labelledby="wissenswertes">
                    <h2 id="wissenswertes">Back to Topic</h2>
                    <h3>Cannabis als Arzneimittel</h3>

                    <p>
                        Dich interessiert die Vielseitigkeit von Cannabis als Arzneimittel, die Geschichte und
                        Entwicklung in der medizinischen und kulturellen Verwendung? Das freut uns besonders, denn uns
                        geht es genauso. In unserer Rubrik "Back to Topic" berichten wir regelmäßig über
                        unterschiedliche Themengebiete. Als alter Hase sind für dich vielleicht Neuentwicklungen aus der
                        Forschung interessant, als noch unerfahrener Patient freust du dich vielleicht über etwas
                        Grundwissen. Wir hoffen, dass für jeden etwas Interessantes dabei ist, jedoch sind einige
                        Inhalte nur registrierten Patienten vorbehalten. Wenn du also noch nicht registriert bist, dann
                        werde jetzt Teil der CANN24-Community.
                    </p>

                    <RouterLink :to="{ name: 'blog' }" class="btn btn-primary">zu "Back to Topic"</RouterLink>
                </article>
            </div>
        </div>
    </main>
</template>
